import { Link, useParams } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { t, LEG_KIND_BG, TRIP_EVENT_BG, countryName } from '@/i18n';
import {
  formatBps,
  formatDateTime,
  formatKm,
  formatMinor,
  formatPlate,
  formatWhen,
} from '@/lib/format';
import { useTrip, useTripRoute, useTripSheet } from '@/api/hooks/trips';
import { TripSheetView } from '@/components/trips/TripSheetView';
import { Badge, StatusPill } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, Stat } from '@/components/ui/Card';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/Table';
import { CardList, CardListItem, NarrowOnly, WideOnly } from '@/components/ui/MobileList';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { LazyMap } from '@/components/map/LazyMap';

export function TripDetailPage() {
  const { tripId } = useParams({ from: '/trips/$tripId' });
  const { data: trip, isLoading, isError, refetch } = useTrip(tripId);
  const sheet = useTripSheet(tripId);
  const route = useTripRoute(tripId);

  if (isLoading) return <LoadingBlock />;
  if (isError || !trip) return <ErrorState onRetry={() => void refetch()} />;

  const km = trip.actualDistanceKm ?? trip.plannedDistanceKm ?? 0;
  const emptyKm = trip.emptyKm ?? 0;
  const emptyShareBps = km > 0 ? Math.round((emptyKm / km) * 10000) : 0;
  const revenue = trip.revenueMinor ?? 0;
  const cost = trip.costMinor ?? 0;
  const margin = trip.marginMinor ?? revenue - cost;
  const marginBps = revenue > 0 ? Math.round((margin / revenue) * 10000) : 0;

  // Липсващ списък не бива да сваля екрана — виж същата бележка в заявката.
  const legs = trip.legs ?? [];
  const orders = trip.orders ?? [];
  const events = trip.events ?? [];

  // Празните отсечки носят разход и нула приход — това е числото, което обяснява месеца.
  const emptyLegCost = legs
    .filter((leg) => leg.kind !== 'loaded')
    .reduce((sum, leg) => sum + leg.costMinor, 0);

  const breakdown = trip.costBreakdown;

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-lg font-bold tracking-tight">{trip.number}</h1>
            <StatusPill status={trip.status} kind="trip" />
          </div>
          <p className="mt-0.5 text-xs text-ink-dim">
            {trip.vehicleFleetNo ?? '—'} · {formatPlate(trip.vehiclePlate)} ·{' '}
            {trip.driverName ?? '—'}
          </p>
        </div>
        <div className="w-full text-xs text-ink-dim sm:ml-auto sm:w-auto sm:text-right">
          <div>
            {t.trips.planned}: {formatWhen(trip.plannedStartAt)} → {formatWhen(trip.plannedEndAt)}
          </div>
          <div>
            {t.trips.started}: {formatWhen(trip.startedAt)} · {t.trips.completed}:{' '}
            {formatWhen(trip.completedAt)}
          </div>
        </div>
      </div>

      {sheet.data ? <TripSheetView sheet={sheet.data} /> : sheet.isLoading ? <LoadingBlock /> : null}

      {trip.status === 'completed' ? (
      <Card>
        <CardHeader title={t.trips.pnl} subtitle={t.trips.emptyLegExplain} />
        <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
          <Stat label={t.trips.revenue} value={formatMinor(revenue, trip.ccy)} />
          <Stat label={t.trips.cost} value={formatMinor(cost, trip.ccy)} />
          <Stat
            label={t.trips.margin}
            value={formatMinor(margin, trip.ccy)}
            hint={formatBps(marginBps)}
            tone={margin < 0 ? 'danger' : 'ok'}
          />
          <Stat label={t.trips.loadedKm} value={formatKm(trip.loadedKm ?? km - emptyKm)} />
          <Stat
            label={t.trips.emptyKm}
            value={formatKm(emptyKm)}
            hint={formatBps(emptyShareBps)}
            tone={emptyShareBps > 3000 ? 'warn' : 'default'}
          />
          <Stat
            label={t.trips.emptyCost}
            value={formatMinor(emptyLegCost, trip.ccy)}
            tone={emptyLegCost > revenue * 0.2 ? 'warn' : 'default'}
          />
        </CardBody>

        {breakdown ? (
          <CardBody className="border-t border-line">
            <div className="mb-2 text-[11px] uppercase tracking-wide text-ink-faint">
              {t.quote.breakdown}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
              {(
                [
                  ['fuel', t.quote.fuel],
                  ['tolls', t.quote.tolls],
                  ['driver', t.quote.driver],
                  ['perDiem', t.quote.perDiem],
                  ['accommodation', t.quote.accommodation],
                  ['fixed', t.quote.fixed],
                  ['other', t.quote.other],
                ] as const
              ).map(([key, label]) => (
                <div key={key}>
                  <div className="text-[10px] uppercase tracking-wide text-ink-faint">{label}</div>
                  <div className="font-mono text-sm tabular">
                    {formatMinor(breakdown[key], trip.ccy)}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        ) : null}
      </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="overflow-hidden lg:col-span-2">
          <CardHeader title={t.trips.legs} />
          <NarrowOnly>
            <CardList>
              {legs
                .slice()
                .sort((a, b) => a.sequence - b.sequence)
                .map((leg) => (
                  <CardListItem
                    key={leg.id}
                    className={leg.kind === 'loaded' ? undefined : 'bg-raised/50'}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-xs text-ink-faint">{leg.sequence}</span>
                      <Badge tone={leg.kind === 'loaded' ? 'ok' : 'warn'}>
                        {LEG_KIND_BG[leg.kind]}
                      </Badge>
                      <span className="ml-auto font-mono text-sm tabular">
                        {formatKm(leg.distanceKm)}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-ink-dim">
                      {leg.fromCity ?? '—'} ({leg.fromCountry ?? '—'}) → {leg.toCity ?? '—'} (
                      {leg.toCountry ?? '—'})
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                      {leg.orderNumber ? (
                        <span className="font-mono">{leg.orderNumber}</span>
                      ) : null}
                      <span className={leg.revenueMinor === 0 ? 'text-ink-faint' : undefined}>
                        {t.trips.revenue}: {formatMinor(leg.revenueMinor, leg.ccy)}
                      </span>
                      <span>
                        {t.trips.cost}: {formatMinor(leg.costMinor, leg.ccy)}
                      </span>
                    </div>
                  </CardListItem>
                ))}
            </CardList>
          </NarrowOnly>
          <WideOnly>
            <Table>
              <THead>
                <TR>
                  <TH>#</TH>
                  <TH>{t.trips.kind}</TH>
                  <TH>{t.orders.route}</TH>
                  <TH>Заявка</TH>
                  <TH numeric>{t.trips.distance}</TH>
                  <TH numeric>{t.trips.revenue}</TH>
                  <TH numeric>{t.trips.cost}</TH>
                </TR>
              </THead>
              <TBody>
                {legs
                  .slice()
                  .sort((a, b) => a.sequence - b.sequence)
                  .map((leg) => (
                    <TR key={leg.id} className={leg.kind === 'loaded' ? undefined : 'bg-raised/50'}>
                      <TD className="font-mono text-xs text-ink-faint">{leg.sequence}</TD>
                      <TD>
                        <Badge tone={leg.kind === 'loaded' ? 'ok' : 'warn'}>
                          {LEG_KIND_BG[leg.kind]}
                        </Badge>
                      </TD>
                      <TD className="whitespace-nowrap text-ink-dim">
                        {leg.fromCity ?? '—'} ({leg.fromCountry ?? '—'}) → {leg.toCity ?? '—'} (
                        {leg.toCountry ?? '—'})
                      </TD>
                      <TD className="font-mono text-xs">{leg.orderNumber ?? '—'}</TD>
                      <TD numeric>{formatKm(leg.distanceKm)}</TD>
                      <TD numeric className={leg.revenueMinor === 0 ? 'text-ink-faint' : undefined}>
                        {formatMinor(leg.revenueMinor, leg.ccy)}
                      </TD>
                      <TD numeric>{formatMinor(leg.costMinor, leg.ccy)}</TD>
                    </TR>
                  ))}
              </TBody>
            </Table>
          </WideOnly>
        </Card>

        <div className="space-y-4">
          <Card className="overflow-hidden">
            <CardHeader title={t.map.replay} />
            <LazyMap className="h-64 w-full" vehicles={[]} route={route.data?.points} />
          </Card>

          <Card>
            <CardHeader title={t.orders.events} />
            <CardBody>
              <ul className="space-y-1.5">
                {events.map((event) => (
                  <li key={event.id} className="flex flex-wrap gap-x-2 text-xs">
                    <span className="w-full shrink-0 font-mono text-ink-faint sm:w-28">
                      {formatDateTime(event.at)}
                    </span>
                    <span className={cn('font-medium', event.type === 'rejected' && 'text-danger')}>
                      {TRIP_EVENT_BG[event.type]}
                    </span>
                    <span className="text-ink-dim">
                      {event.country ? countryName(event.country) : ''} {event.note ?? ''}
                    </span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
